import {
  computed, observable, makeObservable, makeAutoObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { navigate } from 'gatsby';
import { AttachmentType, CPDQuestion, CPDQuestionReq, CPDCategory, PAGE_SIZE, PlatformType, QuestionType, StatusCodes, VmList, VmListT, SpeakerApplicationResponse, SpeakerApplicationStats } from '../constants/options';
import { SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { FAxios } from '../utilities/file-network';
import { isDevelopment } from '../constants/settings';

export default class EventStore {

  loading: boolean = false;

  loadingInfo: boolean = false;

  uploading: boolean = false;
  deletingFile: boolean = false;
  downloading: boolean = false;

  downloadingEventAttachmentNames: string[] = [];

  exportingCPDMembers = false;

  cpdActivitiesCache = new Map<number, any>();
  uploadedEventImgHashCache = new Map<string, string>();

  events: VmList = { totalCount: 0, items: [] };
  members: VmList = { totalCount: 0, items: [] };
  eventInfo: any = null;

  removingMemberFromCPD: boolean = false;

  savingCPDActivity: boolean = false;
  loadingCPDActivity: boolean = false;

  loadingQuiz: boolean = false;
  loadingQuizList: boolean = false;
  savingQuiz: boolean = false;

  calculatingCpdCredit: boolean = false;

  loadingSpeakerApplication: boolean = false;
  savingSpeakerApplication: boolean = false;
  loadingSpeakerApplicationStats: boolean = false;

  uploadingCPDActivityAttachment: boolean = false;
  deletingCPDActivityAttachmentIds: number[] = [];

  updatingDocumentName: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  searchEventList = async (skip: number, take: number, reqModel: {
    title?: string,
    language?: string,
    cpdCategoryIds?: number[],
    packageId?: number,
    hasPackage?: boolean,
    isAvailable?: boolean,
    isArchived?: boolean,
    approvalStatusId?: number,
    hasCertificate?: boolean,
    isPublished?: boolean,
    registerDdlFrom?: string,
    registerDdlTo?: string,
    startAtFrom?: string,
    startAtTo?: string,
    endAtFrom?: string,
    endAtTo?: string,
    presenter?: string,
    organizationId?: number,
    provider?: string,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    showInPayment?: boolean,
  }, signal?: AbortSignal) => {
    this.loading = true;
    try {
      if (isDevelopment) {
        if (reqModel.name) {
          this.rootStore.notify("DEPRECATED FIELD:name", "warning");
        }
      }

      const res = await Axios.post(`/api/Cpd/Search/${skip}/${take}`, reqModel, { signal });

      // res.data.items.forEach((item: any) => {
      //   this.cpdActivitiesCache.set(item.id, item);
      // });

      // this.events = res.data;
      // console.log("maintenance", res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createEvent = async (reqModel: {
    name: string,
    cpdCategoryId: number,
    coverImageId: number,
    organizationId: number,
    presenter: string,
    hasCertificate: boolean,
    registerDdl: string,
    startAt: string,
    endAt: string,
    creditPoints: number,
    zoomNote: string,
    note: string,
    provider: string,
    location: string,
    duration: string,
    pricing: number,
    memberPrice: number,
    recordLink: string,
    zoomLink: string,
    approvalStatusId: number,
    isPublished: boolean,
    fullContent: {
      language: string,
      title: string,
      abstract: string,
      content: string,
      plainContent: string;
    }[],
    tagIds: number[],
    cpdAttachments: any[];
  }) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.post(`/api/Cpd`, reqModel);
      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  getEvent = async (id: number, triggerLoading: boolean = true) => {
    if (triggerLoading) { this.loadingInfo = true; }
    try {
      const res = await Axios.get(`/api/Cpd/${id}`);

      this.cpdActivitiesCache.set(res.data.id, res.data);

      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  updateEvent = async (reqModel: {
    name: string,
    cpdCategoryId: number,
    coverImageId: number,
    organizationId: number,
    presenter: string,
    hasCertificate: boolean,
    registerDdl: string,
    startAt: string,
    endAt: string,
    creditPoints: number,
    zoomNote: string,
    note?: string,
    provider: string,
    location: string,
    duration: string,
    pricing: number,
    memberPrice: number,
    recordLink: string,
    galaPriceGuest: number,
    galaPriceMember: number,
    speechApplicationRules: string,
    zoomLink: string,
    approvalStatusId: number,
    isPublished: boolean,
    id: number,
    fullContent: {
      language: string,
      title: string,
      abstract: string,
      content: string,
      plainContent: string;
    }[];
  } | any) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.put(`/api/Cpd`, reqModel);

      this.cpdActivitiesCache.set(res.data.id, res.data);

      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  toggleCPDPromoted = async (reqModal: { cpdId: number, showInPayment: boolean; }) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.put(`/api/Cpd/Purchasable/`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  archiveEvent = async (id: number) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.put(`/api/Cpd/Archive/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  deleteEvent = async (id: number) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.delete(`/api/Cpd/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  downloadEventAttachment = async (name: string) => {
    this.downloading = true;
    this.downloadingEventAttachmentNames.push(name);
    try {
      const res = await FAxios.get(`/api/Cpd/Attachment/${name}?platformId=${PlatformType.ADMIN_WEB}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
      this.downloadingEventAttachmentNames = this.downloadingEventAttachmentNames.filter(n => n !== name);
    }
  };

  renameEventAttachment = async (reqModel: {
    speechApplicationId: number,
    cpdId: number,
    attachmentCategoryId: number,
    originalName: string,
    id: number
  }) => {
    this.updatingDocumentName = true;
    try {
      const res = await Axios.put(`/api/Cpd/Attachment/?platformId=${PlatformType.ADMIN_WEB}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updatingDocumentName = false;
    }
  };

  uploadEventAttachment = async (file: {
    fileName: string,
    attachmentCategoryId: number,
    file: string,
    cpdId: number
  }) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Attachment?platformId=${PlatformType.ADMIN_WEB}`, file);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };


  deleteEventAttachment = async (name: string) => {
    this.deletingFile = true;
    try {
      const res = await Axios.delete(`/api/Cpd/Attachment/${name}?platformId=${PlatformType.ADMIN_WEB}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingFile = false;
    }
  };

  searchEventMapList = async (skip: number, take: number, reqModel: {
    name?: string,
    title?: string,
    language?: string,
    cpdCategoryIds?: number[] | undefined,
    isAvailable?: boolean,
    isArchived?: boolean,
    approvalStatusId?: number,
    hasCertificate?: boolean,
    isPublished?: boolean,
    registerDdlFrom?: string,
    registerDdlTo?: string,
    startAtFrom?: string,
    startAtTo?: string,
    endAtFrom?: string,
    endAtTo?: string,
    // presenter?: string,
    organizationId?: number,
    provider?: string,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    memberId?: number,
    cpdId?: number,
    showWeightedScore?: boolean,
  }, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Map/Search/${skip}/${take}`, reqModel, { signal: abortSignal });
      this.members = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createEventMap = async (reqModel: any) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.post(`/api/Cpd/Map`, reqModel);
      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  getEventMap = async (id: number) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.get(`/api/Cpd/Map/${id}`);
      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  updateEventMap = async (reqModel: any) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.put(`/api/Cpd/Map`, reqModel);
      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  deleteEventMap = async (id: number) => {
    this.removingMemberFromCPD = true;
    try {
      const res = await Axios.delete(`/api/Cpd/Map/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removingMemberFromCPD = false;
    }
  };


  approveEventMap = async (reqModel: {
    cpdId: number,
    memberIds: number[],
    approvalStatusId: StatusCodes,
  }) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.put(`/api/Cpd/Map/Approval`, reqModel);
      this.eventInfo = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };


  // ============ CPD ACTIVITY ATTACHMENT ============

  searchCPDActivityAttachmentList = async (skip: number, take: number, reqModel: {
    cpdId?: number,
    speechApplicationId?: number,
    attachmentCategoryId?: AttachmentType,
  }) => {
    this.loadingCPDActivity = true;
    try {
      const res = await Axios.post(`/api/Cpd/Attachment/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingCPDActivity = false;
    }
  };

  createCPDActivityAttachment = async (reqModel: {
    fileName: string,
    file: string,
    cpdId: number,
    speechApplicationId?: number,
    attachmentCategoryId?: AttachmentType,
  }) => {
    this.uploadingCPDActivityAttachment = true;
    try {
      const res = await Axios.post(`/api/Cpd/Attachment`, {
        attachmentCategoryId: AttachmentType.CPD,
        ...reqModel,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingCPDActivityAttachment = false;
    }
  };

  updateCPDActivityAttachment = async (reqModel: {
    id: number,
    fileName: string,
    file: string,
    cpdActivityId: number,
    attachmentCategoryId: number,
  }) => {
    this.savingCPDActivity = true;
    try {
      const res = await Axios.put(`/api/Cpd/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.CPD,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingCPDActivity = false;
    }
  };

  deleteCPDActivityAttachment = async (id: number) => {
    this.deletingCPDActivityAttachmentIds.push(id);
    try {
      const res = await Axios.delete(`/api/Cpd/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingCPDActivityAttachmentIds = this.deletingCPDActivityAttachmentIds.filter(i => i !== id);
    }
  };

  searchMemberSubmittedActivityList = async (userId: number, abortSignal?: AbortSignal) => {
    this.loadingCPDActivity = true;
    try {
      const res = await Axios.post(`/api/Cpd/Search/0/0`, {
        cpdCategoryIds: [CPDCategory.PRIVATE_CPD],
        createdBy: userId,
      }, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingCPDActivity = false;
    }
  };

  // generate cpd certificate
  downloadCPDActivityCertificate = async (reqModel: {
    memberId: number,
    cpdId: number,
  }) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/Cpd/Certification/${reqModel.memberId}?cpdId=${reqModel.cpdId}&isPreview=true`, {}, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };


  // ============ CPD QUIZ ============

  searchQuizList = async (skip: number, take: number, reqModel: {
    seqId?: number,
    cpdId?: number,
    typeId?: number,
    question?: string,
    description?: string,
    createdAtFrom?: string,
    createdAtTo?: string,
    createdBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    lastModifiedBy?: number,
  }) => {
    this.loadingQuizList = true;
    try {
      const res = await Axios.post(`/api/Quiz/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQuizList = false;
    }
  };

  createQuiz = async (reqModel: CPDQuestionReq) => {
    this.savingQuiz = true;
    try {
      const res = await Axios.post('/api/Quiz', reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingQuiz = false;
    }
  };

  updateQuiz = async (reqModel: CPDQuestionReq) => {
    this.savingQuiz = true;
    try {
      const res = await Axios.put(`/api/Quiz`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingQuiz = false;
    }
  };

  deleteQuiz = async (id: number) => {
    this.loadingQuiz = true;
    try {
      const res = await Axios.delete(`/api/Quiz/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQuiz = false;
    }
  };

  searchQuizAnswerList = async (skip: number, take: number, reqModel: {
    memberId?: number,
    questionId?: number,
    cpdId?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    createdBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    lastModifiedBy?: number,
    showWeightedScore?: boolean,
  }) => {
    this.loadingQuiz = true;
    try {
      const res = await Axios.post(`/api/Quiz/Answer/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQuiz = false;
    }
  };

  // deleteEventMapMultiple = async (ids: number[]) => {
  //   this.removingMemberFromCPD = true;
  //   try {
  //     const res = await Axios.delete(`/api/Cpd/Map/Multiple`, { data: ids });
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.removingMemberFromCPD = false;
  //   }
  // }

  // ============ calculate cpd credit ============

  calculateCpdCredit = async (memberId: number) => {
    this.calculatingCpdCredit = true;
    try {
      const res = await Axios.post(`/api/Cpd/Credit/Calculate/Member/${memberId}`, {});
      return Promise.resolve(res.data as { cpdLodged: number[], totalCredits: number; });
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.calculatingCpdCredit = false;
    }
  };

  generateInternalCpdPDF = async (reqModal: any) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/cpd/Form/Generate/Internal`, { ...reqModal }, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  generateExternalCpdPDF = async (reqModal: any) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/cpd/Form/Generate`, { ...reqModal }, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  // CPD Registered List

  downloadEventMapList = async (cpdId: number) => {
    this.exportingCPDMembers = true;
    try {
      const res = await Axios.post(`/api/Cpd/Map/Export/${cpdId}`, {}, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.exportingCPDMembers = false;
    }
  };

  // Speaker Application

  searchSpeakerApplicationList = async (skip: number, take: number, reqModel: {
    cpdId?: number,
    memberId?: number,
    title?: string,
    approvalStatusId?: number,
    isPublished?: boolean,
    isHidden?: boolean,
  }) => {
    this.loadingSpeakerApplication = true;
    try {
      const res = await Axios.post<VmListT<SpeakerApplicationResponse>>(`/api/Cpd/Speech/Application/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSpeakerApplication = false;
    }
  };

  createSpeakerApplication = async (reqModel: {
    cpdId: number,
    memberId: number,
    title: string,
    notes: string,
    content: string,
    description: string,
    approvalStatusId: number,
    isPublished: boolean,
    isHidden: boolean
  }) => {
    this.savingSpeakerApplication = true;
    try {
      const res = await Axios.post(`/api/Cpd/Speech/Application`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingSpeakerApplication = false;
    }
  };

  getSpeakerApplicationStats = async (cpdId: number) => {
    this.loadingSpeakerApplicationStats = true;
    try {
      const res = await Axios.get<SpeakerApplicationStats>(`/api/Cpd/Speech/Application/Stats?cpdId=${cpdId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSpeakerApplicationStats = false;
    }
  };

  getSpeakerApplication = async (id: number) => {
    this.loadingSpeakerApplication = true;
    try {
      const res = await Axios.get<SpeakerApplicationResponse>(`/api/Cpd/Speech/Application/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSpeakerApplication = false;
    }
  };

  updateSpeakerApplication = async (reqModel: {
    id: number,
    cpdId: number,
    memberId: number,
    title: string,
    notes: string,
    content: string,
    description: string,
    approvalStatusId: number,
    isPublished: boolean,
    isHidden: boolean
  }) => {
    this.savingSpeakerApplication = true;
    try {
      const res = await Axios.put(`/api/Cpd/Speech/Application`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingSpeakerApplication = false;
    }
  };

  deleteSpeakerApplication = async (id: number) => {
    this.savingSpeakerApplication = true;
    try {
      const res = await Axios.delete(`/api/Cpd/Speech/Application/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingSpeakerApplication = false;
    }
  };
}